import type { RankingControl, RankingControlRow } from '@repo/sdk/gen'
import { Badge } from '@repo/ui/badge'
import { DataTable } from '@repo/ui/components/data-table/data-table'
import type {
	DataTableColumn,
	DataTablePaginationConfig
} from '@repo/ui/components/data-table/types'
import { Link, useNavigate } from '@tanstack/react-router'
import { useTranslation } from '@/lib/i18n'
import { formatScore } from '../-lib/score'

/** The four ranked lists, which have seats. */
export type ListKey = keyof RankingControl['seats']

/** The quota whose seats a list gives. */
export const QUOTA_OF = {
	freshers: 'q30',
	later_years: 'q30',
	doctoral: 'q30',
	q70: 'q70'
} as const satisfies Record<ListKey, 'q30' | 'q70'>

/** The list each of the two that share the rest of the DSU seats shares them with. */
const SHARES_WITH: Partial<Record<ListKey, ListKey>> = {
	later_years: 'doctoral',
	doctoral: 'later_years'
}

/**
 * What a list gives, above its rows: the seats, with whom they are shared,
 * those of its quota nobody took when the page knows them, and what the list
 * is.
 */
function ListHeader({
	kind,
	seats,
	unfilled
}: {
	kind: ListKey
	seats: number
	unfilled?: number
}) {
	const { t } = useTranslation()
	const sharesWith = SHARES_WITH[kind]
	return (
		<div className='flex flex-col gap-1'>
			<p className='flex flex-wrap items-baseline gap-x-2 font-medium text-sm tabular-nums'>
				<span>
					{t('admin-ranking:lists.seats', { count: seats })}
					{sharesWith && (
						<span className='font-normal text-muted-foreground'>
							{' '}
							{t('admin-ranking:lists.shared_with', {
								list: t(
									`admin-ranking:lists.${sharesWith}.title`
								)
							})}
						</span>
					)}
				</span>
				{unfilled !== undefined && unfilled > 0 && (
					<>
						<span aria-hidden className='text-muted-foreground'>
							·
						</span>
						<span className='text-attention-strong'>
							{t(
								`admin-ranking:lists.unfilled_${QUOTA_OF[kind]}`,
								{
									count: unfilled
								}
							)}
						</span>
					</>
				)}
			</p>
			<p className='text-muted-foreground text-sm'>
				{t(`admin-ranking:lists.${kind}.subtitle`)}
			</p>
		</div>
	)
}

/**
 * The rows of a list in the shared table, which only draws the rows on
 * screen: one line each, so what marks a row sits in its own column.
 */
function Rows({
	rows,
	residenceId,
	pagination,
	loading
}: {
	rows: RankingControlRow[]
	residenceId?: string
	/** For a list read a page at a time. */
	pagination?: DataTablePaginationConfig
	loading?: boolean
}) {
	const { t, i18n } = useTranslation()
	const navigate = useNavigate()
	const columns: DataTableColumn<RankingControlRow & { id: string }>[] = [
		{
			id: 'position',
			header: t('admin-ranking:columns.position'),
			cell: (row) => <span className='tabular-nums'>{row.position}</span>,
			width: 72,
			minWidth: 72
		},
		{
			id: 'name',
			header: t('admin-ranking:columns.name'),
			cell: (row) => <Name residenceId={residenceId} row={row} />,
			minWidth: 200,
			flex: 2
		},
		{
			id: 'protocol',
			header: t('admin-ranking:columns.protocol'),
			cell: (row) => <span className='tabular-nums'>{row.protocol}</span>,
			minWidth: 130,
			flex: 1
		},
		{
			id: 'score',
			header: t('admin-ranking:columns.score'),
			cell: (row) => (
				<span className='font-medium tabular-nums'>
					{formatScore(row.score, i18n.language)}
				</span>
			),
			minWidth: 110,
			flex: 1
		},
		{
			id: 'outcome',
			header: t('admin-ranking:columns.outcome'),
			cell: (row) => <OutcomeBadge row={row} />,
			minWidth: 220,
			flex: 1
		},
		{
			id: 'notes',
			header: t('admin-ranking:columns.notes'),
			cell: (row) => <Notes row={row} />,
			minWidth: 260,
			flex: 2
		}
	]
	return (
		<DataTable
			className='max-h-[70vh]'
			columns={columns}
			data={rows.map((row) => ({ ...row, id: row.submissionId }))}
			enableColumnMenu={false}
			onRowClick={
				residenceId
					? (row) =>
							navigate({
								params: {
									residenceId,
									submissionId: row.submissionId
								},
								to: '/bando/$residenceId/domande/$submissionId'
							})
					: undefined
			}
			pagination={pagination}
			state={{ loading, empty: t('admin-ranking:lists.empty') }}
		/>
	)
}

/** The student's name, a link to the application when the page knows the residence. */
function Name({
	row,
	residenceId
}: {
	row: Pick<RankingControlRow, 'firstName' | 'lastName' | 'submissionId'>
	residenceId?: string
}) {
	const name = `${row.lastName} ${row.firstName}`
	if (!residenceId) {
		return name
	}
	return (
		<Link
			className='hover:underline'
			params={{ residenceId, submissionId: row.submissionId }}
			to='/bando/$residenceId/domande/$submissionId'
		>
			{name}
		</Link>
	)
}

function OutcomeBadge({ row }: { row: RankingControlRow }) {
	const { t } = useTranslation()
	if (row.outcome === 'withdrawn') {
		return (
			<Badge variant='neutral'>
				{t('admin-ranking:outcome.withdrawn')}
			</Badge>
		)
	}
	if (row.outcome === 'forfeited') {
		return (
			<Badge variant='neutral'>
				{t('admin-ranking:outcome.forfeited')}
			</Badge>
		)
	}
	if (row.outcome === 'assigned' && row.quota) {
		return (
			<Badge variant='success'>
				{t('admin-ranking:outcome.assigned', {
					quota: row.quota === 'q30' ? '30%' : '70%'
				})}
			</Badge>
		)
	}
	return <Badge variant='neutral'>{t('admin-ranking:outcome.no_seat')}</Badge>
}

/**
 * What marks a row on one line: evaluated with declared data, sent after the
 * call, where its seat came from, the disability priority taken away.
 */
function Notes({ row }: { row: RankingControlRow }) {
	const { t } = useTranslation()
	const seatOrigin =
		row.seatOrigin && row.seatOrigin !== 'ranking_order'
			? t(`admin-ranking:seat_origin.${row.seatOrigin}`)
			: null
	return (
		<span className='flex min-w-0 items-center gap-1'>
			{row.declaredDataUsed && (
				<Badge variant='warning'>{t('admin-ranking:verifying')}</Badge>
			)}
			{row.late && (
				<Badge variant='neutral'>{t('admin-ranking:late')}</Badge>
			)}
			{seatOrigin && (
				<span
					className='truncate text-muted-foreground text-xs'
					title={seatOrigin}
				>
					{seatOrigin}
				</span>
			)}
			{row.disabilityPriorityRevoked && (
				<span className='truncate text-muted-foreground text-xs'>
					{t('admin-ranking:disability_priority_revoked')}
				</span>
			)}
		</span>
	)
}

export { ListHeader, Name, Rows }
